import React, { useState, useRef } from 'react';
import { Upload, X, File, CheckCircle, AlertCircle } from 'lucide-react';
import s3Api from '../services/s3Api';

const FileUpload = ({
  label,
  value,
  onChange,
  filename,
  onFilenameChange,
  onRemove,
  accept = '.pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar',
  maxSize = 10 * 1024 * 1024, // 10MB
  placeholder = "파일을 선택하거나 여기에 드래그하세요"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    const file = files[0];
    if (!file) return;

    // 파일 크기 검증
    if (file.size > maxSize) {
      setError(`파일 크기는 ${Math.round(maxSize / 1024 / 1024)}MB 이하여야 합니다.`);
      return;
    }

    // 파일 타입 검증
    const allowedTypes = accept.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension) && !allowedTypes.includes(file.type)) {
      setError('허용되지 않는 파일 형식입니다.');
      return;
    }

    setError('');
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 백엔드가 폴더/고유명을 생성하므로 원본 파일명만 전달
      const fileName = file.name;
      // S3 서명에 사용된 Content-Type과 업로드 시 보낼 Content-Type을 일치시킴
      const mime = (file.type && file.type.trim()) ? file.type : 'application/octet-stream';

      // 업로드 URL 생성 (백엔드에 원본 파일명과 MIME만 전달)
      const uploadData = await s3Api.generateUploadUrl(fileName, mime);
      console.log(`uploadData: ${uploadData.uploadUrl}`);
      // 파일 업로드
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': mime },
      });

      console.log(`uploadResponse: ${uploadResponse.status}`);
      console.log(`uploadResponse: ${uploadResponse.toString()}`);

      if (uploadResponse.ok) {
        const fileUrl = convertPresignedUrlToPublicUrl(uploadData.uploadUrl).fileUrl;
        onChange(fileUrl, file.name);

        setUploadProgress(100);
      } else {
        // S3 4xx의 상세 원인 파악을 위해 본문도 출력
        const errText = await uploadResponse.text();
        console.error('S3 upload failed:', uploadResponse.status, errText);
        throw new Error('파일 업로드에 실패했습니다.');
      }
    } catch (err) {
      console.error('파일 업로드 오류:', err);
      setError('파일 업로드 중 오류가 발생했습니다.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    handleFileSelect(files);
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      onChange('');
      if (onFilenameChange) {
        onFilenameChange('');
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return decodeURIComponent(pathParts[pathParts.length - 1]);
    } catch {
      return url;
    }
  };

  const convertPresignedUrlToPublicUrl = (presignedUrl) => {
    if (typeof presignedUrl !== 'string' || !presignedUrl.startsWith('http')) {
      console.error('Invalid presigned URL:', presignedUrl);
      return null;
    }

    try {
      const url = new URL(presignedUrl.trim());

      const hostParts = url.hostname.split('.');
      if (hostParts.length < 5) {
        console.error('Unexpected hostname format:', url.hostname);
        return null;
      }

      const bucketName = hostParts[0];
      const region = hostParts[2];
      const filePath = url.pathname.replace(/^\/+/, ''); // remove leading slash

      const fileUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;

      return { fileUrl, bucketName, region, filePath };
    } catch (error) {
      console.error('Failed to parse URL:', error);
      return null;
    }
  }

  // 표시할 파일명 결정 (filename prop 우선, 없으면 URL에서 추출)
  const displayFileName = filename || (value ? getFileNameFromUrl(value) : '');

  return (
    <div className="form-field">
      <label className="text-sm font-medium text-gray-700 mb-2">{label}</label>

      {value ? (
        // 업로드된 파일 표시
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-600" />
            <File size={16} className="text-green-600" />
            <div className="flex flex-col">
              <span className="text-sm text-green-800 font-medium">
                {displayFileName}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 hover:bg-red-200 text-red-500 hover:text-red-700 transition-all duration-200 hover:scale-110"
            title="파일 삭제"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        // 파일 업로드 영역
        <div
          className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${isUploading
            ? 'border-blue-300 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
            }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileInputChange}
            className="hidden"
            style={{ display: 'none' }}
            disabled={isUploading}
            tabIndex={-1}
            aria-hidden="true"
          />
          {isUploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-600">업로드 중...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Upload size={20} className="text-gray-400" />
              <span className="text-sm text-gray-600">파일 업로드</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default FileUpload; 